import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import Button from '@/components/ui/button/Button';
import { withFormProtection } from '@/components/form/withFormProtection';
import { ActivityType } from '../types/activityTypes';
import { useCars } from '@/features/cars/hooks/useCars';
import { CarWithLoadedModel } from '@/features/cars/api/carsApi';
import { useCreateActivity } from '../hooks/useActivities';
import { useUpdateActivity } from '../hooks/useActivities';
import { useToast } from '@/components/ui/toast/Toast';
import TextArea from '@/components/form/input/TextArea';
import AddressAutocomplete from '@/components/form/input/AddressAutocomplete';

interface CreateActivityDto {
  type: ActivityType;
  car_id: string;
  activity_date: string;
  activity_time: string;
  address: string;
  deliver_latitude: string;
  deliver_longitude: string;
  comment: string;
  id?: string;
}

interface FormErrors {
  type?: string;
  car_id?: string;
  activity_date?: string;
  activity_time?: string;
  address?: string;
  deliver_latitude?: string;
  deliver_longitude?: string;
  comment?: string;
}

interface ActivityFormProps {
  onSubmit: (data: CreateActivityDto) => Promise<void>;
  onClose: (hasUnsavedChanges?: boolean) => void;
  initialData: CreateActivityDto;
  currentData?: CreateActivityDto;
  onDataChange?: (data: CreateActivityDto) => void;
}

const defaultFormData: CreateActivityDto = {
  type: 'handoff_to_service',
  car_id: '',
  activity_date: new Date().toISOString().split('T')[0],
  activity_time: '12:00',
  address: '',
  deliver_latitude: '48.8566', // Default to Paris coordinates
  deliver_longitude: '2.3522', // Default to Paris coordinates
  comment: '',
};

const ActivityForm = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, ActivityFormProps>(
  ({ onSubmit, onClose, initialData, onDataChange }, ref) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState<FormErrors>({});
    const [formData, setFormData] = useState<CreateActivityDto>(initialData || defaultFormData);
    const { data: carsResponse, isLoading: isLoadingCars } = useCars({
      limit: 1000, // Загружаем больше автомобилей, чтобы показать все доступные
    });
    const cars = carsResponse || [];

    const ACTIVITY_TYPE_OPTIONS = [
      { value: 'handoff_to_service', label: t('activities.form.options.handoff_to_service') },
      { value: 'receiving_from_service', label: t('activities.form.options.receiving_from_service') },
    ];

    useEffect(() => {
      onDataChange?.(formData);
    }, [formData, onDataChange]);

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {};

      if (!formData.type) {
        newErrors.type = t('activities.form.errors.type_required');
      }
      if (!formData.car_id) {
        newErrors.car_id = t('activities.form.errors.car_required');
      }
      if (!formData.activity_date) {
        newErrors.activity_date = t('activities.form.errors.date_required');
      }
      if (!formData.activity_time) {
        newErrors.activity_time = t('activities.form.errors.time_required');
      }
      if (!formData.address) {
        newErrors.address = t('activities.form.errors.address_required');
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const clearFieldError = (field: keyof FormErrors) => {
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

    const handleTypeChange = (value: string) => {
      setFormData(prev => ({ ...prev, type: value as ActivityType }));
      clearFieldError('type');
    };

    const handleCarChange = (value: string) => {
      setFormData(prev => ({ ...prev, car_id: value }));
      clearFieldError('car_id');
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, activity_date: value }));
      clearFieldError('activity_date');
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, activity_time: value }));
      clearFieldError('activity_time');
    };

    const handleAddressChange = (value: string) => {
      setFormData(prev => ({ ...prev, address: value }));
      clearFieldError('address');
    };

    const handlePlaceSelect = (place: any) => {
      // Формируем строку адреса для поля
      const address =
        place.name && place.formatted_address
          ? `${place.name}, ${place.formatted_address}`
          : place.formatted_address || place.name || '';

      setFormData(prev => ({
        ...prev,
        address,
        deliver_latitude: place.geometry?.location?.lat?.() || '',
        deliver_longitude: place.geometry?.location?.lng?.() || '',
      }));
      clearFieldError('address');
    };

    const handleCommentChange = (value: string) => {
      setFormData(prev => ({ ...prev, comment: value }));
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      try {
        await onSubmit(formData);
      } catch (error) {
        console.error('Failed to create activity:', error);
      }
    };

    return (
      <>
        <div className="px-6 py-4 border-b border-border-primary">
          <h5 className="text-xl font-semibold text-text-primary lg:text-2xl max-w-[80%]">
            {initialData.car_id ? t('activities.form.edit_title') : t('activities.form.create_title')}
          </h5>
          <p className="text-sm text-text-tertiary max-w-[80%] mt-2">
            {initialData.car_id ? t('activities.form.edit_description') : t('activities.form.create_description')}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="col-span-2">
              <Label>{t('activities.form.fields.type')}</Label>
              <Select
                testId="activity-form-field-type"
                options={ACTIVITY_TYPE_OPTIONS}
                onChange={handleTypeChange}
                defaultValue={formData.type}
                placeholder={t('activities.form.placeholders.select_type')}
                error={errors.type}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('activities.form.fields.car')}</Label>
              <Select
                testId="activity-form-field-car"
                options={cars.map((car: CarWithLoadedModel) => ({
                  label: car.model ? `${car.model.brand_name} ${car.model.model_name} (${car.registration_number})` : car.registration_number,
                  value: car.id
                }))}
                onChange={handleCarChange}
                defaultValue={formData.car_id}
                placeholder={isLoadingCars ? t('activities.form.placeholders.loading_cars') : t('activities.form.placeholders.select_car')}
                error={errors.car_id}
                disabled={isLoadingCars || !!initialData.car_id}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('activities.form.fields.date')}</Label>
              <Input
                testId="activity-form-field-date"
                type="date"
                value={formData.activity_date}
                onChange={handleDateChange}
                error={errors.activity_date}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('activities.form.fields.time')}</Label>
              <Input
                testId="activity-form-field-time"
                type="time"
                value={formData.activity_time}
                onChange={handleTimeChange}
                error={errors.activity_time}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('activities.form.fields.address')}</Label>
              <AddressAutocomplete
                testId="activity-form-field-address"
                value={formData.address}
                onChange={handleAddressChange}
                onPlaceSelect={handlePlaceSelect}
                placeholder={t('activities.form.placeholders.search_service')}
                error={errors.address}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('activities.form.fields.comment')}</Label>
              <TextArea
                testId="activity-form-field-comment"
                value={formData.comment}
                onChange={handleCommentChange}
                placeholder={t('activities.form.placeholders.enter_comment')}
                rows={3}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('activities.form.fields.latitude')}</Label>
              <Input
                type="text"
                value={formData.deliver_latitude}
                placeholder={t('activities.form.placeholders.auto_filled')}
                disabled={true}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('activities.form.fields.longitude')}</Label>
              <Input
                type="text"
                value={formData.deliver_longitude}
                placeholder={t('activities.form.placeholders.auto_filled')}
                disabled={true}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border-primary">
          <div className="flex justify-end gap-3">
            <Button
              testId="activity-form-cancel-button"
              variant="outline"
              onClick={() => onClose()}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              testId="activity-form-submit-button"
              variant="primary"
              onClick={handleSubmit}
              disabled={Object.keys(errors).length > 0}
            >
              {initialData.car_id ? t('activities.form.buttons.update') : t('activities.form.buttons.create')}
            </Button>
          </div>
        </div>
      </>
    );
  }
);

ActivityForm.displayName = 'ActivityForm';

const ProtectedActivityForm = withFormProtection(ActivityForm);

interface ActivityFormWrapperProps extends Omit<ActivityFormProps, 'currentData' | 'onDataChange' | 'initialData'> {
  initialData?: CreateActivityDto;
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const ActivityFormWrapper = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, ActivityFormWrapperProps>((props, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [currentData, setCurrentData] = useState<CreateActivityDto>(props.initialData ?? defaultFormData);
  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();

  const handleDataChange = (data: CreateActivityDto) => {
    setCurrentData(data);
  };

  const handleSubmit = async (data: CreateActivityDto) => {
    try {
      const apiPayload: any = {
        id: currentData.id,
        type: data.type,
        car: data.car_id,
        activity_date: data.activity_date,
        activity_time: data.activity_time,
        address: data.address,
        comment: data.comment || '',
        latitude: data.deliver_latitude || '',
        longitude: data.deliver_longitude || '',
      };
      if (currentData.id) {
        await updateActivity.mutateAsync({ id: currentData.id!, data: apiPayload });
      } else {
        await createActivity.mutateAsync(apiPayload);
      }
      props.onClose?.(false);
      toast.success(currentData.id ? t('activities.form.edit_success') : t('activities.form.create_success'));
    } catch (error) {
      toast.error(currentData.id ? t('activities.form.edit_error') : t('activities.form.create_error'));
    }
  };

  return (
    <ProtectedActivityForm
      {...props}
      ref={ref}
      initialData={props.initialData ?? defaultFormData}
      currentData={currentData}
      onDataChange={handleDataChange}
      onSubmit={handleSubmit}
      onClose={(hasUnsavedChanges) => props.onClose?.(hasUnsavedChanges)}
    />
  );
});

ActivityFormWrapper.displayName = 'ActivityFormWrapper';

export default ActivityFormWrapper;
